import { useEffect, useRef, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { http, resolveImage, API_BASE } from "@/lib/api";
import Layout from "@/components/Layout";

export default function QRPurchase() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    http.get(`/qr/scan/${code}`).then(({ data }) => setData(data)).catch(() => setErr(true));
  }, [code]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      const p = a.play();
      if (p && typeof p.catch === "function") {
        p.then(() => setPlaying(true)).catch(() => setPlaying(false));
      } else {
        setPlaying(true);
      }
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  if (err) return (
    <Layout>
      <div className="py-24 text-center">
        <h1 className="text-grunge text-3xl text-[#F5F2EB]">This QR code is no longer active.</h1>
        <p className="mt-4 font-body text-[#A3A3A3]">Browse the full collection instead.</p>
        <Link className="btn-copper mt-6 inline-flex" to="/shop">View Shop</Link>
      </div>
    </Layout>
  );
  if (!data) return <Layout><div className="py-24 text-center font-ui text-[#737373]">Loading…</div></Layout>;

  const art = data.artwork;
  const loc = data.location;
  const mode = data.qr?.mode || "direct";
  if (!art) return <Navigate to="/shop" replace />;

  // Direct mode → straight to artwork detail (current behavior)
  if (mode !== "story") {
    return <Navigate to={`/artwork/${art.slug}?qr=${code}`} replace />;
  }

  // Story mode → immersive landing page
  const audioSrc = art.audio_url
    ? (art.audio_url.startsWith("/api/") ? `${API_BASE}${art.audio_url}` : art.audio_url)
    : null;

  return (
    <Layout>
      <section className="bg-black pt-16 pb-24" data-testid="qr-story-page">
        <div className="max-w-[920px] mx-auto px-6 md:px-10">
          {loc?.business_name && (
            <div className="text-center mb-3 font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055]" data-testid="qr-story-location">
              You found this at · {loc.business_name}
            </div>
          )}
          <div className="text-center mb-1 font-ui text-[10px] tracking-[0.22em] uppercase text-[#737373]">
            Collector Story · #{(art.collection_number || "—").toString().padStart(2, "0")}
          </div>
          <h1 className="text-center text-grunge text-5xl md:text-7xl text-[#F5F2EB] mb-3 leading-[1.05]">
            {art.title?.toUpperCase()}
          </h1>
          <p className="text-center font-script text-2xl md:text-3xl text-[#D4B07B] mb-10">
            {art.short_description || "A truth lives here."}
          </p>

          <div className="mb-10 relative">
            <img
              src={resolveImage(art.main_image)}
              alt={art.title}
              className="w-full max-h-[560px] object-cover border border-white/10"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
          </div>

          {audioSrc && (
            <div className="max-w-[640px] mx-auto mb-10" data-testid="qr-story-audio">
              <div className="border border-[#C9A055]/40 bg-[#0a0a0a] p-5 flex items-center gap-4">
                <button
                  type="button"
                  onClick={togglePlay}
                  className="w-14 h-14 rounded-full border-2 border-[#C9A055] text-[#C9A055] flex items-center justify-center hover:bg-[#C9A055] hover:text-black transition-colors shrink-0"
                  aria-label={playing ? "Pause" : "Play"}
                  data-testid="qr-story-audio-toggle"
                >
                  {playing ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14"/><rect x="14" y="5" width="4" height="14"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-1">Hear it · in Mickey’s voice</div>
                  <div className="font-body text-sm text-[#B5B0A7] truncate">Tap play. Take a minute. Then decide.</div>
                  <div className="mt-2 h-[3px] bg-white/10 overflow-hidden">
                    <div className="h-full bg-[#C9A055] transition-[width] duration-200" style={{ width: `${progress}%` }} />
                  </div>
                </div>
                <audio
                  ref={audioRef}
                  src={audioSrc}
                  preload="none"
                  onTimeUpdate={e => {
                    const a = e.currentTarget;
                    if (a.duration > 0) setProgress((a.currentTime / a.duration) * 100);
                  }}
                  onEnded={() => { setPlaying(false); setProgress(100); }}
                  onPause={() => setPlaying(false)}
                  onPlay={() => setPlaying(true)}
                />
              </div>
            </div>
          )}

          {art.story && (
            <div className="max-w-[640px] mx-auto" data-testid="qr-story-body">
              <div className="font-body text-[17px] leading-[1.85] text-[#E8E4DC] whitespace-pre-line">
                {art.story}
              </div>
            </div>
          )}

          <div className="mt-14 flex flex-col items-center gap-4">
            <Link
              to={`/artwork/${art.slug}?qr=${code}`}
              className="btn-copper text-base px-10 py-4"
              data-testid="qr-story-continue-btn"
            >
              CONTINUE · OWN THIS TRUTH →
            </Link>
            <Link to="/shop" className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#737373] hover:text-[#C9A055]">
              Or browse the full collection
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
